import React, { useState, useEffect } from 'react';
import {
  Headphones,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  Send,
  User,
  Building2,
  ChevronRight,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import MSPLayout from '../components/MSPLayout';
import {
  getMSPSupportTickets,
  getMSPTicketDetail,
  updateMSPTicket,
  replyToMSPTicket,
  getMSPSupportTeam,
} from '../services/api';
import { toast } from 'sonner';

const statusColors = {
  open: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  in_progress: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  resolved: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  closed: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
};

const priorityColors = {
  low: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  medium: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  high: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
};

export default function MSPSupport() {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [team, setTeam] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  useEffect(() => {
    fetchTickets();
    fetchTeam();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await getMSPSupportTickets();
      setTickets(response.data.tickets || []);
      setStats(response.data.stats || { open: 0, in_progress: 0, resolved: 0, closed: 0 });
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view support tickets');
      } else {
        toast.error('Failed to load support tickets');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await getMSPSupportTeam();
      setTeam(response.data.team || []);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await getMSPTicketDetail(ticketId);
      setSelectedTicket(response.data);
      setShowTicketDetail(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await updateMSPTicket(ticketId, { status: newStatus });
      toast.success('Ticket status updated');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  const handleAssign = async (ticketId, memberId, memberName) => {
    try {
      await updateMSPTicket(ticketId, { assigned_to: memberId, assigned_to_name: memberName });
      toast.success('Ticket assigned');
      setShowAssignDialog(false);
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, assigned_to: memberId, assigned_to_name: memberName });
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      await replyToMSPTicket(selectedTicket.id, { message: replyMessage });
      toast.success('Reply sent');
      setReplyMessage('');
      fetchTicketDetail(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = searchTerm === '' || 
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.ticket_number?.toString().includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  return (
    <MSPLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-2xl text-white">Support Center</h1>
            <p className="text-sm text-slate-400">Manage support tickets from your clients</p>
          </div>
          <Button onClick={fetchTickets} variant="outline" className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.open}</p>
                  <p className="text-xs text-slate-500">Open</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.in_progress}</p>
                  <p className="text-xs text-slate-500">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.resolved}</p>
                  <p className="text-xs text-slate-500">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                  <Headphones className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{tickets.length}</p>
                  <p className="text-xs text-slate-500">Total Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tickets Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Support Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-12">
                <Headphones className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No tickets found</h3>
                <p className="text-slate-400">
                  {tickets.length === 0 
                    ? "Your clients haven't submitted any support tickets yet."
                    : "No tickets match your current filters."
                  }
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Ticket #</TableHead>
                    <TableHead className="text-slate-400">Client</TableHead>
                    <TableHead className="text-slate-400">Subject</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Priority</TableHead>
                    <TableHead className="text-slate-400">Assigned To</TableHead>
                    <TableHead className="text-slate-400">Created</TableHead>
                    <TableHead className="text-slate-400"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow 
                      key={ticket.id} 
                      className="border-slate-800 cursor-pointer hover:bg-slate-800/50"
                      onClick={() => fetchTicketDetail(ticket.id)}
                    >
                      <TableCell className="font-mono text-slate-400">
                        #{ticket.ticket_number}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          <span className="text-white">{ticket.client_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-white font-medium max-w-[250px] truncate">
                        {ticket.subject}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[ticket.status]}>
                          {ticket.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={priorityColors[ticket.priority]}>
                          {ticket.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {ticket.assigned_to_name || 'Unassigned'}
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ticket Detail Sheet */}
      <Sheet open={showTicketDetail} onOpenChange={setShowTicketDetail}>
        <SheetContent className="bg-slate-900 border-slate-800 w-full sm:max-w-xl overflow-y-auto">
          {selectedTicket && (
            <>
              <SheetHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-400 font-mono">#{selectedTicket.ticket_number}</p>
                    <SheetTitle className="text-white text-xl mt-1">{selectedTicket.subject}</SheetTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-400">{selectedTicket.client_name}</span>
                    </div>
                  </div>
                </div>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Ticket Meta */}
                <div className="flex flex-wrap gap-3">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Status</p>
                    <Select 
                      value={selectedTicket.status} 
                      onValueChange={(v) => handleStatusChange(selectedTicket.id, v)}
                    >
                      <SelectTrigger className="w-[130px] bg-slate-800 border-slate-700 text-white h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Priority</p>
                    <Badge className={priorityColors[selectedTicket.priority]}>
                      {selectedTicket.priority}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Category</p>
                    <Badge variant="outline" className="border-slate-700">
                      {selectedTicket.category}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Assigned To</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAssignDialog(true)}
                      className="border-slate-700 h-6 text-xs"
                    >
                      {selectedTicket.assigned_to_name || 'Assign'}
                    </Button>
                  </div>
                </div>

                {/* Conversation */}
                <div>
                  <h4 className="text-sm font-medium text-white mb-3">Conversation</h4>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {selectedTicket.messages?.map((msg) => (
                      <div 
                        key={msg.id}
                        className={`p-3 rounded-lg ${
                          msg.sender_type === 'customer' 
                            ? 'bg-slate-800 border border-slate-700' 
                            : 'bg-blue-500/10 border border-blue-500/20'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="text-sm font-medium text-white">{msg.sender_name}</span>
                            <Badge variant="outline" className="border-slate-700 text-xs">
                              {msg.sender_type === 'customer' ? 'Client' : 'MSP Agent'}
                            </Badge>
                          </div>
                          <span className="text-xs text-slate-500">
                            {new Date(msg.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-slate-300 text-sm whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reply */}
                {selectedTicket.status !== 'closed' && (
                  <div className="pt-4 border-t border-slate-800">
                    <Textarea
                      placeholder="Type your reply..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white min-h-[100px] mb-3"
                    />
                    <div className="flex justify-end gap-2">
                      <Button 
                        onClick={handleReply}
                        disabled={!replyMessage.trim() || sending}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {sending ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Reply
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Ticket</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            {team.map((member) => (
              <Button
                key={member.id}
                variant="outline"
                className="w-full justify-start border-slate-700 text-left"
                onClick={() => handleAssign(selectedTicket?.id, member.id, member.name)}
              >
                <User className="w-4 h-4 mr-2" />
                <span className="flex-1">{member.name}</span>
                <Badge variant="outline" className="border-slate-700 text-xs">
                  {member.role?.replace('msp_', '')}
                </Badge>
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="border-slate-700">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MSPLayout>
  );
}
